import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getDb } from './firebase';
import type { QuoteEmailTemplate, GeneralSettings } from '../types';

const EMAIL_TEMPLATES_DOC = 'settings/email_templates';
const GENERAL_SETTINGS_DOC = 'settings/general';

/**
 * Fetches the quote email template from Firestore.
 * Returns null if the settings document doesn't exist yet.
 */
export async function getEmailTemplate(): Promise<QuoteEmailTemplate | null> {
  const snap = await getDoc(doc(getDb(), EMAIL_TEMPLATES_DOC));
  if (!snap.exists()) return null;
  const data = snap.data() as { quoteEmailTemplate?: QuoteEmailTemplate };
  return data?.quoteEmailTemplate ?? null;
}

/**
 * Saves the quote email template to Firestore, merging into the settings document
 * so other future settings fields are preserved.
 */
export async function saveEmailTemplate(
  template: Pick<QuoteEmailTemplate, 'subject' | 'html'>,
  adminEmail: string
): Promise<void> {
  const updated = {
    ...template,
    updatedAt: serverTimestamp(),
    updatedBy: adminEmail,
  };
  await setDoc(doc(getDb(), EMAIL_TEMPLATES_DOC), { quoteEmailTemplate: updated }, { merge: true });
}

/**
 * Fetches the general settings document from Firestore.
 * Returns null if the document doesn't exist yet.
 */
export async function getGeneralSettings(): Promise<GeneralSettings | null> {
  const snap = await getDoc(doc(getDb(), GENERAL_SETTINGS_DOC));
  if (!snap.exists()) return null;
  return snap.data() as GeneralSettings;
}

/**
 * Saves the general settings to Firestore.
 */
export async function saveGeneralSettings(
  settings: Pick<GeneralSettings, 'supportEmail' | 'externalUrls'>,
  adminEmail: string
): Promise<void> {
  await setDoc(
    doc(getDb(), GENERAL_SETTINGS_DOC),
    {
      ...settings,
      updatedAt: serverTimestamp(),
      updatedBy: adminEmail,
    },
    { merge: true }
  );
}
